import React from 'react';
import { Check } from 'lucide-react';
import { Button } from '@components/ui/button';
import { cn } from '@utils/index';
import { FormField, FormItem, FormControl } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';
import { UseFormReturn, FieldValues, Path } from 'react-hook-form';

interface PasswordInputProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  onSubmit: () => void;
  criteriaWrapperClassName?: string;
  buttonClassName?: string;
  passwordLabel?: string;
  isSubmitDisabled?: boolean;
  children?: React.ReactNode;
}

const PasswordCriteria = ({
  isValid,
  label,
}: {
  isValid: boolean;
  label: string;
}) => (
  <div
    className={`flex items-center justify-center gap-0.5 px-2.5 h-6 rounded-full text-[9px] font-medium w-fit !font-secondary ${
      isValid ? 'bg-primary text-white' : 'bg-gray-200 text-gray-600'
    }`}
  >
    <Check
      className={`w-3.5 h-3.5 ${isValid ? 'text-white' : 'text-[#a6a5a0]'}`}
      strokeWidth={3}
    />
    {label}
  </div>
);

export const PasswordInputOnly = <T extends FieldValues>({
  form,
  label = 'Password',
}: {
  form: UseFormReturn<T>;
  label?: string;
}) => {
  const {
    clearErrors,
  } = form;
  return <FormField
    control={form.control}
    name={"password" as Path<T>}
    render={({ field, fieldState }) => (
      <FormItem>
        <FormControl>
          <FloatingInput
            id="password"
            label={label}
            type="password"
            value={field.value}
            onChange={(e) => {
              field.onChange(e);
              clearErrors("password" as Path<T>)
            }}
            onBlur={field.onBlur}
            name={field.name}
            inputClass="border border-[#B7B7B7] h-16 rounded-lg p-2 w-full"
          />
        </FormControl>
        {fieldState.error && (
          <p className="text-primary text-sm mt-1">
            {fieldState.error.message}
          </p>
        )}
      </FormItem>
    )}
  />
};

export const PasswordValidationAndSubmit = <T extends FieldValues>({
  form,
  onSubmit,
  criteriaWrapperClassName,
  buttonClassName,
  buttonText = 'SUBMIT',
  isSubmitDisabled,
  children,
}: {
  form: UseFormReturn<T>;
  onSubmit: () => void;
  criteriaWrapperClassName?: string;
  buttonClassName?: string;
  disabled?: boolean;
  buttonText?: string;
  isSubmitDisabled?: boolean;
  children?: React.ReactNode;
}) => {
  const password = (form.watch("password" as Path<T>) as string) || '';

  const isLengthValid = password.length >= 8;
  const hasUpperLower = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9\s]/.test(password);

  return (
    <>
      <div className="space-y-4 w-fit flex flex-col">
        <div
          className={cn(
            'flex flex-wrap gap-2 mt-5 max-sm:flex-col',
            criteriaWrapperClassName
          )}
        >
          <PasswordCriteria
            isValid={isLengthValid}
            label="At least 8 characters"
          />
          <PasswordCriteria
            isValid={hasUpperLower}
            label="Upper and lower case"
          />
          <PasswordCriteria
            isValid={hasNumber && hasSymbol}
            label="A number and special character"
          />
        </div>
      </div>
      {children}
      <div className="w-full flex max-w-[430px] mt-12 items-center justify-center lg:justify-start">
        <Button
          type="submit"
          onClick={onSubmit}
          className={cn(
            'w-full bg-primary disabled:text-[#ffffff55] disabled:opacity-100 h-14.5 max-w-70 text-white text-[19px] font-extrabold py-2 mt-0 rounded-full max-md:max-w-full',
            buttonClassName
          )}
          disabled={isSubmitDisabled}
        >
          {buttonText}
        </Button>
      </div>
    </>
  );
};

export const PasswordInputWithValidation = <T extends FieldValues>({
  form,
  onSubmit,
  criteriaWrapperClassName,
  buttonClassName,
  passwordLabel,
  isSubmitDisabled,
  children,
}: PasswordInputProps<T>) => {
  return (
    <>
      <PasswordInputOnly form={form} label={passwordLabel} />
      <PasswordValidationAndSubmit
        form={form}
        onSubmit={onSubmit}
        criteriaWrapperClassName={criteriaWrapperClassName}
        buttonClassName={buttonClassName}
        isSubmitDisabled={isSubmitDisabled}
      >
        {children}
      </PasswordValidationAndSubmit>
    </>
  );
};
